import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Output,
  signal,
} from '@angular/core';
import { from, take, catchError, of, finalize } from 'rxjs';
import { Store } from 'src/app/shared/interfaces';
import {
  AuthService,
  EmployeeService,
  FichajeService,
  StoreService,
} from 'src/app/shared/services';
import { DeviceService } from '../../services/device.service';
import { SelectModule } from 'primeng/select';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { GoogleMapsModule } from '@angular/google-maps';
import { GoogleMapsService } from '../../views/stores/google-maps-loader.service';
import { mapStyle } from 'src/environments/variables';
import { FormsModule } from '@angular/forms';
import { FichajeSessionEvent } from 'src/app/shared/interfaces/session-event.interface';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { MessageModule } from 'primeng/message';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'app-fichaje-start',
  providers: [MessageService, ConfirmationService],
  imports: [
    SelectModule,
    CommonModule,
    ConfirmDialogModule,
    ButtonModule,
    ToastModule,
    SkeletonModule,
    FormsModule,
    GoogleMapsModule,
    MessageModule,
  ],
  templateUrl: './fichaje-start.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FichajeStartComponent {
  @Output() fichaje = new EventEmitter<boolean>();

  fichando = false;
  location = '';
  ip = '';
  deviceInfo = '';
  horaActual = new Date();

  private authService = inject(AuthService);
  private storeService = inject(StoreService);
  private deviceService = inject(DeviceService);
  private fichajeService = inject(FichajeService);
  private employeeService = inject(EmployeeService);
  private googleMapsService = inject(GoogleMapsService);
  private ms = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  public user$ = computed(() => this.authService.currentUser());

  public stores: Store[] = [];
  public isLoading = signal<boolean>(true);
  public hasError = signal<boolean>(false);
  public googleLoaded = signal(false);
  public isUploading = false;

  public selectedStore: Store | null = null;
  public recommendedStore: Store | null = null;
  private userLocation: google.maps.LatLngLiteral | null = null;

  async prepararFichaje() {
    this.fichando = true;
    this.horaActual = new Date();
    const [locationStr, ip, deviceInfo] = await Promise.all([
      this.deviceService.getLocation(),
      this.deviceService.getPublicIP(),
      Promise.resolve(this.deviceService.getDeviceInfo()),
    ]);

    this.location = locationStr;
    this.ip = ip;
    this.deviceInfo = deviceInfo;

    const locationLiteral = await this.deviceService.getLocationLiteral();
    if (locationLiteral) {
      this.userLocation = locationLiteral;
      this.center = locationLiteral;
      this.addUserMarker(locationLiteral);
      this.updateRecommendedStore();
    }
  }

  async ngOnInit() {
    this.isLoading.set(true);
    await this.prepararFichaje();

    // Cargar Google Maps antes de renderizar el componente del mapa
    try {
      await this.googleMapsService.load();
      this.googleLoaded.set(true);
    } catch (e) {
      console.error('No se pudo cargar Google Maps:', e);
      this.hasError.set(true);
    }
    from(this.storeService.retrieveStores())
      .pipe(
        take(1),
        catchError((error) => {
          this.hasError.set(true);
          return of([]);
        }),
        finalize(() => {
          this.isLoading.set(false);
        })
      )
      .subscribe((res) => {
        this.stores = res;
        // this.selectedStore = this.stores.find((store) =>
        //   store.uid === this.user$()!.referenceStore
        //     ? this.user$()!.referenceStore
        //     : this.user$()?.current_store
        // )!;
        this.addStoreMarkers();
        this.updateRecommendedStore();
      });
  }

  // MAPS

  zoom = 10;
  center: google.maps.LatLngLiteral = { lat: 40.4168, lng: -3.7038 }; // fallback: Madrid
  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    zoomControl: true,
    scrollwheel: false,
    disableDoubleClickZoom: true,
    streetViewControl: false,
    styles: mapStyle,
    maxZoom: 20,
    minZoom: 6,
  };

  markers: Array<{
    position: google.maps.LatLngLiteral;
    title: string;
    icon: string;
    label?: google.maps.MarkerLabel;
  }> = [];

  private addUserMarker(pos: google.maps.LatLngLiteral) {
    this.markers.push({
      position: pos,
      title: 'Mi ubicación',
      icon: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
      label: {
        color: '#000',
        text: 'Tú',
      },
    });
  }

  async handleFichaje() {
    if (!this.selectedStore) return;
    const canStart = await this.fichajeService.puedeFicharNuevaEntrada(
      this.user$()!.uid
    );
    if (!canStart) {
      this.ms.add({
        severity: 'warn',
        summary: 'Fichaje',
        detail: 'Tienes un fichaje abierto. Cierra antes de iniciar otro.',
      });
      return;
    }
    const session: FichajeSessionEvent = {
      type: 'fichaje',
      date: this.horaActual.getTime(),
      userId: this.user$()!.uid,
      storeId: this.selectedStore!.uid,
      action: 'startwork',
      location: this.location,
      deviceInfo: this.deviceInfo,
      ip: this.ip,
      manualOverride: false,
    };
    this.isUploading = true;
    try {
      if (
        this.selectedStore &&
        this.selectedStore.uid !== this.user$()!.current_store
      ) {
        const res = await this.employeeService.updateUserField(
          this.user$()!.uid,
          { current_store: this.selectedStore.uid }
        );
        if (!res) {
          this.ms.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Ha ocurrido un error al actualizar la tienda',
          });
          return;
        }
      }
      const res = await this.fichajeService.registrarFichaje(session);
      if (res) {
        this.fichaje.emit(true);
        this.ms.add({
          severity: 'success',
          summary: 'Fichaje',
          detail: 'El fichaje ha sido registrado',
        });
      } else {
        this.ms.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Ha ocurrido un error al registrar el fichaje',
        });
      }
      this.isUploading = false;
    } catch (error) {
      this.ms.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Ha ocurrido un error al registrar el fichaje',
      });
      this.isUploading = false;
    }
  }

  private addStoreMarkers() {
    for (const store of this.stores) {
      this.markers.push({
        position: { lat: store.lat, lng: store.lng },
        title: store.name,
        icon: 'https://firebasestorage.googleapis.com/v0/b/luxor-66cbe.appspot.com/o/marker.png?alt=media&token=f64d8393-8069-4620-808c-51eafc1ab72b',
        label: {
          color: '#0B2B23',
          text: store.name,
        },
      });
    }
  }

  private updateRecommendedStore() {
    if (!this.userLocation || !this.stores.length) {
      this.recommendedStore = null;
      return;
    }
    const nearest = this.stores.reduce(
      (acc, store) => {
        const distance = this.calculateDistanceKm(
          this.userLocation!.lat,
          this.userLocation!.lng,
          store.lat,
          store.lng
        );
        if (distance < acc.distance) {
          return { store, distance };
        }
        return acc;
      },
      { store: this.stores[0], distance: Number.POSITIVE_INFINITY }
    );
    this.recommendedStore = nearest.store;
  }

  private calculateDistanceKm(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const toRad = (value: number) => (value * Math.PI) / 180;
    const R = 6371; // km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  confirm1(event: Event) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message:
        'Parece que no estás fichando en tu tienda más cercana. ¿Seguro que quieres continuar?',
      header: 'Confirmar fichaje',
      closable: true,
      closeOnEscape: true,
      icon: 'pi pi-exclamation-triangle',
      rejectButtonProps: {
        label: 'Cancelar',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Confirmar fichaje',
      },
      accept: async () => {
        await this.handleFichaje();
      },
      reject: () => {
      },
    });
  }
}
